import type { RescuedLegacyShrinkOperation } from "~/store/shrinkMobileSlice";
import type { RefireLegacyShrinkOperation } from "~mobile/availability/shrink/hooks/useShrinkMutations";

import { useEffect } from "react";

import logger from "~/helpers/logger";
import { useAppDispatch, useAppSelector } from "~/hooks/reduxHooks";
import { clearRescuedShrinkOperation, selectPendingRescueShrinkOperations } from "~/store/shrinkMobileSlice";
import useShrinkMutations from "~mobile/availability/shrink/hooks/useShrinkMutations";

const reportRescueFailure = (
	operation: RescuedLegacyShrinkOperation,
	errorMessage: string,
	sessionRenewed: boolean
): void => {
	logger.error("[shrink] rescued legacy queue entry failed to replay — the write never landed", {
		errorMessage,
		operation: { id: operation.id, queuedAt: operation.queuedAt, type: operation.type },
		sessionRenewed
	});
};

const drainRescuedShrinkOperation = async (
	operation: RescuedLegacyShrinkOperation,
	refireRescuedShrinkOperation: RefireLegacyShrinkOperation,
	settleRescuedOperation: (id: string) => void
): Promise<void> => {
	const outcome = await refireRescuedShrinkOperation(operation);
	if (outcome.outcome === "failed") reportRescueFailure(operation, outcome.errorMessage, outcome.sessionRenewed);
	settleRescuedOperation(operation.id);
};

const useShrinkLegacyQueueRescue = (): void => {
	const dispatch = useAppDispatch();
	const pendingRescue = useAppSelector(selectPendingRescueShrinkOperations);
	const { refireRescuedShrinkOperation } = useShrinkMutations();

	useEffect(() => {
		pendingRescue.forEach((operation) => {
			void drainRescuedShrinkOperation(operation, refireRescuedShrinkOperation, (id) =>
				dispatch(clearRescuedShrinkOperation(id))
			);
		});
	}, []);
};

export default useShrinkLegacyQueueRescue;
